"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = ReactFlashlight;

var _react = _interopRequireDefault(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _resizeObserverPolyfill = _interopRequireDefault(require("resize-observer-polyfill"));

function _interopRequireDefault(obj) {
  return obj && obj.__esModule ? obj : {
    "default": obj
  };
}
/**
 * 
 * @param {HTMLElement} children The component's children
 * @param {bool} showCursor If true, shows the cursor
 * @param {number} size the initial size of the light
 * @param {object} initialPosition An object {x: value, y: value} defining the initial position
 * @param {object} moveTo An object {x: value, y: value} defining the location to where the light will be moved
 * @param {number} speed Defines the transition speed of the movement of the light
 * @param {bool} contain If true, the light can't move outside of the container
 * @param {bool} enableMouse If true, the user can control the light with its mouse
 * @param {number} darkness Defines how dark is the "room"
 */


function ReactFlashlight(props) {
  var enabled = props.enabled,
      children = props.children,
      showCursor = props.showCursor,
      size = props.size,
      initialPosition = props.initialPosition,
      moveTo = props.moveTo,
      speed = props.speed,
      enableMouse = props.enableMouse,
      darkness = props.darkness;
  var lightStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    // To control the size of the light, simply use a percentage on the background creating the effect - init with size
    background: "radial-gradient(transparent 0%, rgba(0, 0, 0, " + darkness + ") " + size + "px, rgba(0, 0, 0, " + (darkness + 0.1) + ") 80%)",
    transition: "none",
    pointerEvents: "none",
    willChange: "transform"
  };

  var elements = _react["default"].Children.map(children, function (child) {
    return {
      light: _react["default"].useRef(),
      container: _react["default"].useRef()
    };
  });

  _react["default"].useEffect(function () {
    var last_known_scroll_position = 0;
    var ticking = false;
    elements.forEach(function (element) {
      var container = element.container.current;
      container.style.overflow = "hidden";
      container.style.position = "relative";
      if (!showCursor) container.style.cursor = "none";
    }); // Resizes the light

    function resizeLights() {
      elements.forEach(function (element) {
        resizeLight(element);
      });
    }

    function resizeLight(element) {
      var light = element.light.current;
      var maskSize = window.innerWidth > window.innerHeight ? window.innerWidth : window.innerHeight;
      light.style.width = maskSize * 2 + "px";
      light.style.height = maskSize * 2 + "px";
      light.style.left = initialPosition.x - maskSize + "px";
      light.style.top = initialPosition.y - maskSize + "px";
    }

    function handleMouseMove(e) {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          ticking = false;
          elements.forEach(function (element) {
            var light = element.light.current;
            var container = element.container.current;
            var lightStyle = window.getComputedStyle(light, null);
            var containerStyle = container.getBoundingClientRect();
            light.style.transition = "opacity ease-in-out " + speed + "ms";
            light.style.left = e.clientX - containerStyle.left - parseInt(lightStyle.width) / 2 + "px";
            light.style.top = e.clientY - containerStyle.top - parseInt(lightStyle.height) / 2 + "px";
          });
        });
        ticking = true;
      }
    }

    function handleScroll(e) {
      var increment = window.scrollY - last_known_scroll_position;
      last_known_scroll_position = window.scrollY;

      if (!ticking) {
        window.requestAnimationFrame(function () {
          ticking = false;
          elements.forEach(function (element) {
            var light = element.light.current;
            light.style.transition = "opacity ease-in-out " + speed + "ms";
            light.style.top = parseInt(light.style.top) + increment + "px";
          });
        });
        ticking = true;
      }
    }

    resizeLights();
    var resizeObservers = [children.length];
    elements.forEach(function (element, i) {
      resizeObservers[i] = new _resizeObserverPolyfill["default"](function () {
        return resizeLights();
      });
    });
    if (enableMouse) window.addEventListener("mousemove", handleMouseMove);
    if (enableMouse) window.addEventListener('scroll', handleScroll);
    window.addEventListener("resize", resizeLights);
    elements.forEach(function (element, i) {
      resizeObservers[i].observe(element.container.current);
    }); // Cleanup

    return function () {
      if (enableMouse) window.removeEventListener("mousemove", handleMouseMove);
      if (enableMouse) window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", resizeLights);
      elements.forEach(function (element, i) {
        resizeObservers[i].disconnect;
      });
    };
  }, []);
  /**
   * This is executed when moveTo props change
   */


  _react["default"].useEffect(function () {
    if (moveTo) {
      elements.forEach(function (element) {
        var light = element.light.current;
        light.style.transition = "all ease-in-out " + speed + "ms";
        var lightStyle = window.getComputedStyle(light, null);
        light.style.left = moveTo.x - parseInt(lightStyle.width) / 2 + "px";
        light.style.top = moveTo.y - parseInt(lightStyle.height) / 2 + "px";
      });
    }
  }, [moveTo]);

  _react["default"].useEffect(function () {
    elements.forEach(function (element) {
      var light = element.light.current;
      light.style.transition = "opacity ease-in-out " + speed + "ms";
      enabled ? light.style.opacity = "1" : light.style.opacity = "0";
    });
  }, [enabled]);

  return _react["default"].Children.map(children, function (child, i) {
    return _react["default"].cloneElement(child, {
      ref: elements[i].container,
      children: _react["default"].createElement(_react["default"].Fragment, null, _react["default"].createElement("div", {
        "data-testid": "react-flashlight",
        style: lightStyle,
        ref: elements[i].light
      }), child.props.children)
    });
  });
}

ReactFlashlight.propTypes = {
  enabled: _propTypes["default"].bool,
  children: _propTypes["default"].node,
  showCursor: _propTypes["default"].bool,
  size: _propTypes["default"].number,
  initialPosition: _propTypes["default"].shape({
    x: _propTypes["default"].number,
    y: _propTypes["default"].number
  }),
  moveTo: _propTypes["default"].object,
  speed: _propTypes["default"].number,
  contain: _propTypes["default"].bool,
  enableMouse: _propTypes["default"].bool,
  darkness: _propTypes["default"].number
};
ReactFlashlight.defaultProps = {
  enabled: true,
  children: _react["default"].createElement("div", null),
  showCursor: false,
  size: 150,
  initialPosition: {
    x: 0,
    y: 0
  },
  moveTo: null,
  speed: 1000,
  enableMouse: true,
  darkness: 0.9
};